import React, { useEffect, useState } from "react";
import { Clock, Coffee, Crosshair, LogIn, LogOut, MapPin, ShieldCheck, Wifi } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { MobilePage, GlassCard } from "@/components/mobile/MobilePrimitives";

function nowTime() {
  return new Date().toLocaleTimeString("en-PH", { hour: "2-digit", minute: "2-digit" });
}

function todayLabel() {
  return new Date().toLocaleDateString("en-PH", { weekday: "long", month: "long", day: "numeric" });
}

export default function MobileClockIn() {
  const { user, companies, activeCompanyId } = useAuth();
  const company = companies?.find((c) => c.id === activeCompanyId);

  const [time, setTime] = useState(nowTime());
  const [status, setStatus] = useState(null);
  const [gps, setGps] = useState(null);
  const [busy, setBusy] = useState(false);
  const [wfh, setWfh] = useState(false);

  const employeeId = user?.employee_id || user?.id;
  const clockedIn = !!status?.attendance?.time_in && !status?.attendance?.time_out;
  const breaks = status?.attendance?.breaks || [];
  const onBreak = breaks.length > 0 && !breaks[breaks.length - 1].end;

  const loadStatus = async () => {
    try {
      const { data } = await api.get("/timekeeping/clock-status", {
        params: employeeId ? { employee_id: employeeId } : {},
      });
      setStatus(data);
    } catch {}
  };

  const requestGps = () => {
    if (!navigator.geolocation) {
      toast.error("GPS is not supported");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGps({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
        });
      },
      () => toast.error("Unable to capture location"),
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  };

  useEffect(() => {
    const t = setInterval(() => setTime(nowTime()), 1000);
    loadStatus();
    requestGps();
    return () => clearInterval(t);
  }, []);

  const submit = async () => {
    setBusy(true);
    try {
      const payload = {
        employee_id: employeeId,
        latitude: gps?.latitude,
        longitude: gps?.longitude,
        accuracy: gps?.accuracy,
        source: "mobile_pwa",
        ...(clockedIn ? {} : { work_from_home: wfh }),
      };

      await api.post(
        clockedIn ? "/timekeeping/attendance/check-out" : "/timekeeping/attendance/check-in",
        payload
      );

      toast.success(clockedIn ? "Clocked out" : "Clocked in");
      await loadStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unable to submit attendance");
    } finally {
      setBusy(false);
    }
  };

  const toggleBreak = async () => {
    setBusy(true);
    try {
      await api.post(`/timekeeping/attendance/break-${onBreak ? "out" : "in"}`, { employee_id: employeeId });
      toast.success(onBreak ? "Back from break" : "Break started");
      await loadStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unable to record the break");
    } finally {
      setBusy(false);
    }
  };

  return (
    <MobilePage>
      <div className="relative overflow-hidden rounded-[32px] bg-gradient-to-br from-blue-600 via-cyan-500 to-teal-400 p-5 text-white shadow-xl">
        <div className="absolute -right-10 -top-10 h-32 w-32 rounded-full bg-white/20 blur-2xl" />
        <div className="relative">
          <div className="text-sm font-bold text-white/80">Today</div>
          <div className="mt-1 text-5xl font-black">{time}</div>
          <div className="mt-1 text-sm text-white/85">{todayLabel()}</div>
          <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-white/20 px-3 py-1.5 text-xs font-bold">
            <Wifi className="h-4 w-4" />
            GPS-verified attendance
          </div>
        </div>
      </div>

      <GlassCard className="p-5 text-center">
        <div className={`mx-auto flex h-28 w-28 items-center justify-center rounded-full text-white shadow-xl ${
          clockedIn ? "bg-gradient-to-br from-orange-500 to-red-500" : "bg-gradient-to-br from-emerald-500 to-teal-500"
        }`}>
          {clockedIn ? <LogOut className="h-12 w-12" /> : <LogIn className="h-12 w-12" />}
        </div>

        <div className="mt-5 text-sm font-bold text-slate-500">Current Status</div>
        <div className="mt-1 text-3xl font-black">{clockedIn ? "Clocked In" : "Not Clocked In"}</div>
        <div className="mt-1 text-sm text-slate-500">
          {clockedIn ? "Tap below when your shift is done." : "Tap below to start your workday."}
        </div>

        <button
          disabled={busy}
          onClick={submit}
          className={`mt-6 flex w-full items-center justify-center gap-2 rounded-3xl px-5 py-5 text-lg font-black text-white shadow-xl disabled:opacity-60 ${
            clockedIn ? "bg-gradient-to-r from-orange-500 to-red-500" : "bg-gradient-to-r from-blue-600 to-cyan-500"
          }`}
        >
          {clockedIn ? <LogOut className="h-6 w-6" /> : <LogIn className="h-6 w-6" />}
          {busy ? "Submitting..." : clockedIn ? "Clock Out" : "Clock In"}
        </button>

        {!clockedIn && !status?.attendance?.time_in && (
          <label className="mt-4 flex items-center justify-center gap-2 text-sm font-bold text-slate-600">
            <input type="checkbox" checked={wfh} onChange={(e) => setWfh(e.target.checked)} />
            I am working from home today
          </label>
        )}

        {clockedIn && (
          <button
            disabled={busy}
            onClick={toggleBreak}
            className={`mt-3 flex w-full items-center justify-center gap-2 rounded-3xl px-5 py-4 text-base font-black shadow disabled:opacity-60 ${
              onBreak ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-700"
            }`}
          >
            <Coffee className="h-5 w-5" />
            {onBreak ? "End Break" : "Start Break"}
          </button>
        )}
      </GlassCard>

      <div className="grid grid-cols-2 gap-3">
        <GlassCard className="p-4">
          <div className="flex items-center gap-2 text-sm font-black">
            <MapPin className="h-5 w-5 text-blue-600" />
            Location
          </div>
          <div className="mt-2 text-xs text-slate-500">
            {gps ? `${gps.latitude.toFixed(5)}, ${gps.longitude.toFixed(5)}` : "No GPS yet."}
          </div>
          <button onClick={requestGps} className="mt-3 rounded-2xl bg-blue-50 px-3 py-2 text-xs font-black text-blue-700">
            Retry GPS
          </button>
        </GlassCard>

        <GlassCard className="p-4">
          <div className="flex items-center gap-2 text-sm font-black">
            <ShieldCheck className="h-5 w-5 text-emerald-600" />
            Accuracy
          </div>
          <div className="mt-2 text-2xl font-black">{gps?.accuracy ? `${Math.round(gps.accuracy)}m` : "Idle"}</div>
          <div className="mt-1 text-xs text-slate-500">Attached to attendance</div>
        </GlassCard>
      </div>

      <GlassCard className="p-4">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-cyan-100 text-cyan-700">
            <Crosshair className="h-5 w-5" />
          </div>
          <div>
            <div className="text-sm font-black">{company?.name || "Company"} Attendance</div>
            <div className="text-xs text-slate-500">Your clock events are timestamped and secured.</div>
          </div>
        </div>
      </GlassCard>
    </MobilePage>
  );
}
